import * as React from 'react';
import {
  DialogTrigger as RACDialogTrigger,
  Modal as RACModal,
  ModalOverlay as RACModalOverlay,
  Dialog as RACDialog,
  Heading,
  type ModalOverlayProps,
} from 'react-aria-components';

import { cn } from '../lib/cn';

/**
 * Klaret Drawer (handoff §10.8 "Drawer — right-side").
 * Primitive: RAC Modal + ModalOverlay positioned as a bottom sheet (mobile-first).
 * --tvl-raised, --tvl-r4 on top corners, --tvl-e3.
 * Entrance: slide up from bottom (d-slow).
 * All compound names (DrawerTrigger, DrawerPortal, DrawerClose, DrawerOverlay,
 * DrawerContent, DrawerHeader, DrawerFooter, DrawerTitle, DrawerDescription)
 * preserved for existing consumers. vaul removed entirely.
 */

export interface DrawerProps extends Omit<ModalOverlayProps, 'className'> {
  /** Kept for API compat — vaul's `shouldScaleBackground` is a no-op here. */
  shouldScaleBackground?: boolean;
}

export const Drawer: React.FC<DrawerProps> = ({
  shouldScaleBackground: _ignored,
  children,
  ...props
}) => (
  <RACModalOverlay
    className={cn(
      'fixed inset-0 z-[var(--tvl-z-drawer)] flex items-end justify-center',
      'bg-[var(--tvl-scrim)]',
      'entering:animate-[overlay-enter_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
      'exiting:animate-[overlay-exit_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
    )}
    {...props}
  >
    {children}
  </RACModalOverlay>
);
Drawer.displayName = 'Drawer';

export const DrawerTrigger = RACDialogTrigger;

/** @deprecated Portal is implicit in RAC Modal — kept for API compat. */
export const DrawerPortal: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);

export const DrawerClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'rounded-[var(--tvl-r1)] p-1 text-[var(--tvl-ink-3)]',
      'transition-colors duration-[var(--tvl-d-fast)]',
      'hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
      className,
    )}
    aria-label={props['aria-label'] ?? 'Close'}
    {...props}
  >
    {children}
  </button>
));
DrawerClose.displayName = 'DrawerClose';

export const DrawerOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'fixed inset-0 z-[var(--tvl-z-drawer)] bg-[var(--tvl-scrim)]',
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = 'DrawerOverlay';

export const DrawerContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { children?: React.ReactNode }
>(({ className, children, ...props }, _ref) => (
  <RACModal
    className={cn(
      'relative w-full max-h-[90dvh] flex flex-col mt-24',
      'bg-[var(--tvl-raised)] text-[var(--tvl-ink)]',
      /* r4 on top corners only */
      'rounded-t-[var(--tvl-r4)] shadow-[var(--tvl-e3)]',
      'border border-[var(--tvl-hairline)]',
      /* entrance: slide up */
      'entering:animate-[drawer-enter_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
      'exiting:animate-[drawer-exit_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
      className,
    )}
  >
    {/* min-h-0 is load-bearing: this dialog is itself a flex ITEM of the modal above, and a flex
        item's default `min-height: auto` refuses to shrink below its content height. Without it a
        tall drawer body grows straight past the modal's max-h-[90dvh] and spills off the viewport —
        no bottom edge, no footer, and any `overflow-y-auto` on a child never activates because the
        child is never constrained. Measured: with it, a long body scrolls and the footer stays in
        frame; without it, neither does. */}
    <RACDialog className="flex h-full min-h-0 flex-col outline-none" {...(props as Record<string, unknown>)}>
      {() => (
        <>
          {/* drag handle */}
          <div className="mx-auto mt-3 h-1 w-12 shrink-0 rounded-full bg-[var(--tvl-hairline)]" />
          {children}
        </>
      )}
    </RACDialog>
  </RACModal>
));
DrawerContent.displayName = 'DrawerContent';

/* ── Structural slots ────────────────────────────────────────────────────── */
export function DrawerHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('grid gap-1.5 p-4 text-left', className)} {...props} />;
}

export function DrawerFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  // The `tvl-drawer-footer` marker lets globals.css swap the global width-growing
  // hover pop (scale 1.04) for a contained vertical lift on these full-bleed footer
  // buttons — a scale would push a full-width button past, or get clipped flush
  // against, the drawer frame; a lift never changes width, so it stays in-frame.
  // overflow-hidden is kept as a belt-and-suspenders clip for any edge case.
  return <div className={cn('tvl-drawer-footer mt-auto flex flex-col gap-2 overflow-hidden p-4', className)} {...props} />;
}

export const DrawerTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <Heading
    ref={ref}
    slot="title"
    className={cn('text-lg font-semibold leading-none text-[var(--tvl-ink)]', className)}
    {...props}
  />
));
DrawerTitle.displayName = 'DrawerTitle';

export const DrawerDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-[var(--tvl-ink-2)]', className)}
    {...props}
  />
));
DrawerDescription.displayName = 'DrawerDescription';
